import type {
  ProposalLineageProjection,
  ResearchAgentAssessment,
  ResearchLineageNodeProjection,
  RunProjection
} from "@roboscientist/contracts";
import { lineageRefKey } from "./researchLineageLayout.js";

export type AssessmentTrajectoryPoint = {
  id: string;
  nodeKey: string;
  nodeTitle: string;
  assessment: ResearchAgentAssessment;
  delta: 0 | 1;
  score: number;
};

export type PrimaryMetricChange = {
  name: string;
  firstValue: number;
  lastValue: number;
  delta: number;
  observationCount: number;
};

export type RunLifecycle = "active" | "failed" | "completed" | "observed";
export type RunBinding = "bound" | "unassigned" | "orphaned" | "observed";

export type RunFieldEntry = {
  run: RunProjection;
  lifecycle: RunLifecycle;
  binding: RunBinding;
  selectedEvidence: boolean;
  descriptorWarning: boolean;
};

export type LineageNodeRunSummary = {
  activeCount: number;
  failedCount: number;
  interruptedCount: number;
  descriptorWarningCount: number;
  historyIssueCount: number;
  assessmentReviewCount: number;
};

const parsedTime = (value: string | null | undefined): number => {
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const runObservedAt = (run: RunProjection): number =>
  parsedTime(run.startedAt ?? run.completedAt ?? run.updatedAt);

const normalizedRunStatus = (run: RunProjection): string =>
  (run.lifecycle.status || run.status).toLowerCase();

export const runLifecycle = (run: RunProjection): RunLifecycle => {
  const status = normalizedRunStatus(run);
  if (["active", "running"].includes(status)) return "active";
  if (["failed", "error", "cancelled", "canceled", "interrupted"].includes(status)) return "failed";
  if (run.completedAt || ["completed", "success", "succeeded"].includes(status)) return "completed";
  return "observed";
};

export function summarizeLineageNodeRuns(
  node: ResearchLineageNodeProjection
): LineageNodeRunSummary {
  const summary: LineageNodeRunSummary = {
    activeCount: 0,
    failedCount: 0,
    interruptedCount: 0,
    descriptorWarningCount: 0,
    historyIssueCount: 0,
    assessmentReviewCount: 0
  };
  for (const entry of node.runEvidence) {
    const status = normalizedRunStatus(entry.run);
    const active = ["active", "running"].includes(status);
    const failed = ["failed", "error"].includes(status);
    const interrupted = ["interrupted", "cancelled", "canceled"].includes(status);
    const descriptorWarning = entry.run.descriptorState === "invalid"
      || entry.run.descriptorState === "stale";
    if (active) summary.activeCount += 1;
    if (failed) summary.failedCount += 1;
    if (interrupted) summary.interruptedCount += 1;
    if (descriptorWarning) summary.descriptorWarningCount += 1;
    if (failed || interrupted || descriptorWarning) summary.historyIssueCount += 1;
    const lifecycle = runLifecycle(entry.run);
    const terminal = lifecycle === "failed" || lifecycle === "completed";
    if (
      terminal
      && (entry.run.kind === "training" || entry.run.kind === "evaluation")
      && !entry.referencedByAssessment
    ) summary.assessmentReviewCount += 1;
  }
  return summary;
}

/**
 * Builds a monotonic reward trajectory exclusively from each node's latest
 * explicit Agent assessment. Only an explicit improvement earns reward; Run
 * metrics and all other assessment outcomes leave the reward unchanged.
 */
export function buildAssessmentTrajectory(nodes: ResearchLineageNodeProjection[]): AssessmentTrajectoryPoint[] {
  const assessed = nodes.flatMap((node) => node.latestAssessment ? [{ node, assessment: node.latestAssessment }] : [])
    .sort((left, right) => parsedTime(left.assessment.assessedAt) - parsedTime(right.assessment.assessedAt)
      || left.node.ref.experimentId.localeCompare(right.node.ref.experimentId)
      || left.node.ref.nodeId.localeCompare(right.node.ref.nodeId));
  let score = 0;
  return assessed.map(({ node, assessment }) => {
    const delta = assessment.outcome === "improved" ? 1 : 0;
    score += delta;
    return {
      id: `${node.ref.experimentId}/${node.ref.nodeId}:${assessment.id}`,
      nodeKey: lineageRefKey(node.ref),
      nodeTitle: node.title,
      assessment,
      delta,
      score
    };
  });
}

export function buildPrimaryMetricChanges(runs: RunProjection[]): PrimaryMetricChange[] {
  const observations = new Map<string, number[]>();
  const orderedRuns = [...runs]
    .sort((left, right) => runObservedAt(left) - runObservedAt(right) || left.id.localeCompare(right.id));
  for (const run of orderedRuns) {
    const name = run.primaryMetricName?.trim();
    const value = run.primaryMetricValue;
    if (!name || value === null || !Number.isFinite(value)) continue;
    const values = observations.get(name) ?? [];
    values.push(value);
    observations.set(name, values);
  }
  return [...observations.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([name, values]) => {
      const firstValue = values[0]!;
      const lastValue = values.at(-1)!;
      return {
        name,
        firstValue,
        lastValue,
        delta: lastValue - firstValue,
        observationCount: values.length
      };
    });
}

export function buildRunFieldEntries(
  lineage: ProposalLineageProjection,
  runs: RunProjection[]
): RunFieldEntry[] {
  const evidence = new Map<string, { selected: boolean }>();
  for (const node of lineage.nodes) {
    for (const entry of node.runEvidence) {
      const current = evidence.get(entry.run.id);
      evidence.set(entry.run.id, { selected: Boolean(current?.selected || entry.selectedByLatestAssessment) });
    }
  }
  const unassignedIds = new Set(lineage.unassignedRuns.map((run) => run.id));
  const orphanedIds = new Set(lineage.orphanedRuns.map((run) => run.id));
  return [...runs]
    .sort((left, right) => runObservedAt(left) - runObservedAt(right) || left.id.localeCompare(right.id))
    .map((run) => {
      const bound = evidence.get(run.id);
      return {
        run,
        lifecycle: runLifecycle(run),
        binding: orphanedIds.has(run.id)
          ? "orphaned"
          : bound
            ? "bound"
            : unassignedIds.has(run.id)
              ? "unassigned"
              : "observed",
        selectedEvidence: bound?.selected ?? false,
        descriptorWarning: run.descriptorState === "invalid" || run.descriptorState === "stale"
      };
    });
}

export type CumulativeRunPath = {
  line: string;
  area: string;
  first: string;
  last: string;
};

export function buildCumulativeRunPath(
  runs: RunProjection[],
  width: number,
  height: number,
  domainRuns?: RunProjection[]
): CumulativeRunPath {
  const dated = runs.filter((run) => runObservedAt(run) > 0)
    .sort((left, right) => runObservedAt(left) - runObservedAt(right));
  if (!dated.length) return { line: "", area: "", first: "—", last: "—" };
  const sampleCount = Math.min(32, dated.length);
  const sampleIndexes = Array.from({ length: sampleCount }, (_, index) =>
    Math.round(index * (dated.length - 1) / Math.max(1, sampleCount - 1)));
  const domain = domainRuns?.filter((run) => runObservedAt(run) > 0)
    .sort((left, right) => runObservedAt(left) - runObservedAt(right));
  const scaleRuns = domain?.length ? domain : dated;
  const firstTime = runObservedAt(scaleRuns[0]!);
  const lastTime = runObservedAt(scaleRuns.at(-1)!);
  const duration = Math.max(1, lastTime - firstTime);
  const points = sampleIndexes.map((datedIndex) => ({
    x: (runObservedAt(dated[datedIndex]!) - firstTime) / duration * width,
    y: height - (datedIndex + 1) / scaleRuns.length * (height - 12) - 6
  }));
  const line = points.map((point, index) =>
    `${index ? "L" : "M"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(" ");
  const formatDate = (timestamp: number) => new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
  return {
    line,
    area: `${line} L ${domainRuns && dated.length < scaleRuns.length ? points.at(-1)!.x.toFixed(1) : width} ${height} L 0 ${height} Z`,
    first: formatDate(firstTime),
    last: formatDate(runObservedAt(dated.at(-1)!))
  };
}
